import React, { useState, useEffect } from "react";
import { ActiveTab } from "../components/layout/ActiveTab/ActiveTab";
import { Topbar } from "../components/layout/TopBar";
import DashboardPage from "./DashboardPage/DashboardPage";
import { useLocation, useNavigate } from "react-router-dom";
import LearningPageContainer from "./Learnings/LearningPageContainer";
import ProfilePageContainer from "./Profile/ProfilePageContainer";

const PageContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Define valid main tabs that can be highlighted
  const validMainTabs = ["dashboard", "learning", "reports", "announcements", "chat"];
  
  // Extract the current tab from the URL path and handle trailing slash
  const getCurrentTabFromPath = () => {
    // Remove any trailing slash first, except for root path
    let path = location.pathname;
    if (path.length > 1 && path.endsWith('/')) {
      path = path.slice(0, -1);
    }
    
    // Extract the path segment after removing the leading slash
    path = path.slice(1);
    const firstSegment = path.split('/')[0] || "dashboard";
    
    // Return null for non-main tabs (like profile)
    return validMainTabs.includes(firstSegment) ? firstSegment : null;
  };
  
  const [currentTab, setCurrentTab] = useState(getCurrentTabFromPath());
  
  // Handle trailing slash redirection and tab updates
  useEffect(() => {
    // Redirect if there's a trailing slash (except for root path)
    if (location.pathname.length > 1 && location.pathname.endsWith('/')) {
      navigate(location.pathname.slice(0, -1), { replace: true });
      return;
    }
    
    // Otherwise update the tab if needed
    const pathTab = getCurrentTabFromPath();
    if (pathTab !== currentTab) {
      setCurrentTab(pathTab);
    }
  }, [location.pathname, navigate, currentTab]);

  const handleTabChange = (tabId) => {
    setCurrentTab(tabId);
    navigate(`/${tabId}`);
  };

  // Update the renderContent function
  const renderContent = () => {
    // Get the first path segment
    const firstPathSegment = location.pathname.split('/').filter(segment => segment)[0] || "";
    
    // Check if we're in the profile section
    if (firstPathSegment === "profile") {
      return <ProfilePageContainer />;
    }
    
    // Check if we're in the learning section
    if (firstPathSegment === "learning") {
      return <LearningPageContainer />;
    }
    
    // For other sections, use the existing mapping
    const contentMapping = {
      "dashboard": <DashboardPage />,
      "reports": <div className="p-6">Reports Content</div>,
      "announcements": <div className="p-6">Announcements Content</div>,
      "chat": <div className="p-6">Chat Content</div>,
    };

    // Render the content based on the exact path
    if (contentMapping[firstPathSegment]) {
      return contentMapping[firstPathSegment];
    }

    // If no content exists, show "Page Not Found"
    return (
      <div className="flex flex-col items-center justify-center h-96 p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center max-w-md w-full">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Page Not Found</h2>
          <p className="text-gray-700 mb-6">
            The page you are looking for doesn't exist or is currently unavailable.
          </p>
          <button
            onClick={() => handleTabChange("dashboard")}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Topbar />
      <ActiveTab onTabChange={handleTabChange} initialTab={currentTab} />
      <main className="flex-1">
        {renderContent()}
      </main>
    </div>
  );
};

export default PageContainer;